import StaticPage, { Section } from "../components/StaticPage";
import { Link } from "react-router-dom";

const FAMILIES = [
    {
        slug: "indica",
        name: "Indica",
        eyebrow: "Heavy · sedative · evening",
        body: "Indica-dominant strains tend toward the body — heavier, sedating, often nudging you toward couch or sleep. Most carry higher myrcene (an herbal terpene also found in mangoes and lemongrass) and caryophyllene. Best paired with quiet evenings, slow dinners, and not driving anywhere.",
        examples: [
            "Granddaddy Purple — classic grape-leaning indica, late-night staple",
            "Northern Lights — clean, sleepy, low-stimulation",
            "Bubba Kush — heavy body, coffee-and-cocoa nose",
            "Wedding Cake — vanilla-sweet indica-hybrid, very approachable",
        ],
    },
    {
        slug: "sativa",
        name: "Sativa",
        eyebrow: "Bright · creative · daytime",
        body: "Sativa-dominant strains skew toward the head — brighter, more cerebral, often pairing well with conversation, work, or movement. Often heavier in limonene (citrus) and pinene (pine). Use them when you've got things to do, not when you're winding down.",
        examples: [
            "Sour Diesel — sharp gasoline-citrus, NY legend",
            "Jack Herer — pine-forward, focused, classically motivating",
            "Acapulco Gold — archival landrace, warm and uplifting",
            "Super Lemon Haze — bright, talky, social-leaning",
        ],
    },
    {
        slug: "hybrid",
        name: "Hybrid",
        eyebrow: "Balanced · most modern strains",
        body: "Almost every strain on a modern menu is technically a hybrid — pure indicas and pure sativas are rare these days. Hybrids range from indica-leaning to sativa-leaning to true 50/50 balance. Look at terpene profile + lineage, not just the label, to know what you&rsquo;re actually in for.",
        examples: [
            "Blue Dream — sativa-leaning hybrid, NY-and-CA workhorse",
            "Gelato — dessert-sweet, balanced, very photogenic",
            "GG #4 — heavier hybrid, piney, full-body",
            "Pink Runtz — fruity, balanced, modern-shelf staple",
        ],
    },
];

const TERPENE_TABLE = [
    ["Myrcene", "Earthy, herbal — sedative, indica-typical"],
    ["Limonene", "Citrus — bright, mood-lifting, sativa-typical"],
    ["Caryophyllene", "Black-pepper, woody — calming, anti-inflammatory"],
    ["Pinene", "Pine, rosemary — focus, alertness"],
    ["Linalool", "Lavender, floral — calming, sleep-supporting"],
    ["Terpinolene", "Apple, floral — uplifting, creative"],
    ["Humulene", "Hops, woody — appetite-suppressing, anti-inflammatory"],
];

export default function Strains() {
    return (
        <StaticPage
            title="Strain Library · Indica, Sativa, Hybrid · ZenZest"
            description="A plain-English strain library from ZenZest. Indica, sativa, hybrid — what each really means, the terpenes that drive the effect, and representative strains on our shelves."
            canonical="https://weeddispensarynearme.com/strains"
            eyebrow="Strain library"
            h1="Read the terpenes, not the label."
            intro="The indica / sativa / hybrid taxonomy is useful shorthand, but the real story is the terpene profile. Here's how we explain it at the counter."
            cta={[
                { label: "Shop flower", href: "/menu/queens-long-island?category=FLOWER", primary: true },
                { label: "Learn more", href: "/learn" },
            ]}
            toc={[
                { id: "indica", label: "Indica" },
                { id: "sativa", label: "Sativa" },
                { id: "hybrid", label: "Hybrid" },
                { id: "terpenes", label: "Terpene cheat sheet" },
            ]}
        >
            {FAMILIES.map((f) => (
                <Section key={f.slug} id={f.slug} title={f.name}>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-zen-olive">
                        {f.eyebrow}
                    </p>
                    <p>{f.body}</p>
                    <ul className="space-y-2 text-sm">
                        {f.examples.map((e) => (
                            <li key={e} className="flex gap-2">
                                <span className="text-zen-olive">·</span>
                                <span>{e}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="not-prose">
                        <Link
                            to={`/menu/queens-long-island?category=FLOWER`}
                            className="text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 inline-block"
                        >
                            Browse {f.name.toLowerCase()} flower
                        </Link>
                    </p>
                </Section>
            ))}

            <Section id="terpenes" title="Terpene cheat sheet">
                <p>
                    Terpenes are the aromatic compounds in cannabis (and pretty much
                    every other plant) that drive how a strain actually feels. If
                    you&rsquo;ve found a strain you love, look at its terpene profile —
                    you&rsquo;ll likely love anything with a similar dominant terpene.
                </p>
                <div className="not-prose border border-zen-line bg-zen-surface">
                    {TERPENE_TABLE.map(([name, desc], i) => (
                        <div
                            key={name}
                            className={`grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 px-5 py-3 text-sm ${
                                i < TERPENE_TABLE.length - 1 ? "border-b border-zen-line" : ""
                            }`}
                        >
                            <p className="font-serif text-zen-ink font-medium">
                                {name}
                            </p>
                            <p className="text-zen-ink-soft">{desc}</p>
                        </div>
                    ))}
                </div>
            </Section>
        </StaticPage>
    );
}
